/**
 * Day 7, defensible findings, from candidate generation onward: each
 * candidate's support, counter-evidence and limitations before the
 * skeptical review controls, the immutable approved findings, and the
 * review manifest's honest outcome.
 */

import type { ReactNode } from 'react';

import { ANALYSIS_STAGES } from '@mouja/domain/analysis';

import {
  generateAnalysisFindingCandidates,
  newIdempotencyKey,
  reviewAnalysisFindings,
  type AnalysisDay7State,
  type AnalysisRunSummary,
} from '../../api.js';
import { Button, InlineAlert, Tag, TextAreaField } from '../../ui.js';
import { SectionHeading } from '../common.js';
import { parseObjectJson } from './review-json.js';
import { card } from './run-page-styles.js';
import { toneFor } from './run-state-labels.js';
import type { RunMutation } from './useAnalysisRunMutation.js';
import type { ReviewDrafts } from './useReviewDrafts.js';

export function FindingsReviewSection({
  id,
  run,
  day7,
  busy,
  mutate,
  drafts,
}: {
  readonly id: string;
  readonly run: AnalysisRunSummary;
  readonly day7: AnalysisDay7State;
  readonly busy: string | null;
  readonly mutate: RunMutation;
  readonly drafts: ReviewDrafts;
}): ReactNode {
  const { findingsReviewJson, setFindingsReviewJson } = drafts;
  if (
    ANALYSIS_STAGES.findIndex((stage) => stage === run.currentStage) <
    ANALYSIS_STAGES.indexOf('FINDING_CANDIDATES')
  ) {
    return null;
  }

  return (
    <section id="findings-review" style={{ marginBottom: 'var(--space-lg)' }}>
      <SectionHeading>Defensible findings review</SectionHeading>
      <div style={{ ...card, display: 'grid', gap: 12 }}>
        <p style={{ color: 'var(--text-secondary)', margin: 0 }}>
          Candidates come only from deterministic Day-6 measurements. A finding exists
          only after its support, counter-evidence, scope and limitations survive Owner
          review. This stage does not create opportunities or recommendations.
        </p>
        {run.currentStage === 'FINDING_CANDIDATES' &&
        run.executionState === 'PENDING' ? (
          <Button
            variant="primary"
            busy={busy === 'finding-candidates'}
            onClick={() =>
              void mutate('finding-candidates', () =>
                generateAnalysisFindingCandidates(id, newIdempotencyKey()),
              )
            }
          >
            Generate bounded finding candidates
          </Button>
        ) : null}
      </div>

      {day7.candidates.length > 0 ? (
        <div style={{ display: 'grid', gap: 12, marginTop: 12 }}>
          {day7.candidates.map((candidate) => {
            const payload = candidate.payload;
            const statement =
              typeof payload['statement'] === 'string'
                ? payload['statement']
                : 'Candidate wording unavailable';
            const eligibility =
              typeof payload['eligibility'] === 'object' &&
              payload['eligibility'] !== null &&
              !Array.isArray(payload['eligibility'])
                ? (payload['eligibility'] as Record<string, unknown>)
                : {};
            const eligibilityState =
              typeof eligibility['state'] === 'string'
                ? eligibility['state']
                : 'UNKNOWN';
            return (
              <article key={candidate.id} style={card}>
                <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                  <Tag tone={toneFor(eligibilityState)}>{eligibilityState}</Tag>
                  <Tag tone="info">
                    {typeof payload['family'] === 'string'
                      ? payload['family']
                      : 'UNKNOWN_FAMILY'}
                  </Tag>
                  <Tag tone="info">
                    {typeof payload['scope'] === 'string'
                      ? payload['scope']
                      : 'UNKNOWN_SCOPE'}
                  </Tag>
                </div>
                <h3 style={{ font: 'var(--type-heading-sm)' }}>{statement}</h3>
                <h4>Support</h4>
                <pre style={{ whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>
                  {JSON.stringify(payload['support'] ?? {}, null, 2)}
                </pre>
                <h4>Counter-evidence</h4>
                <pre style={{ whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>
                  {JSON.stringify(payload['counterEvidence'] ?? [], null, 2)}
                </pre>
                <h4>Limitations</h4>
                <pre style={{ whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>
                  {JSON.stringify(payload['limitations'] ?? [], null, 2)}
                </pre>
                <p style={{ color: 'var(--text-muted)', marginBottom: 0 }}>
                  Candidate artifact {candidate.id} · immutable input hash{' '}
                  {candidate.contentHash}
                </p>
              </article>
            );
          })}
        </div>
      ) : null}

      {run.currentStage === 'FINDINGS_REVIEW' &&
      run.executionState === 'AWAITING_REVIEW' ? (
        <div style={{ ...card, display: 'grid', gap: 12, marginTop: 12 }}>
          <InlineAlert
            tone="warning"
            title="Nothing is pre-approved. Eligible candidates default to REJECTED; insufficient candidates default to INSUFFICIENT_EVIDENCE."
          />
          <p style={{ color: 'var(--text-secondary)', margin: 0 }}>
            Change a decision to APPROVED only after inspecting the visible support,
            counter-evidence and limitations. CORRECTED may alter only wording, scope,
            or family; it cannot alter measurements or evidence.
          </p>
          <TextAreaField
            label="Findings-review decisions JSON"
            value={findingsReviewJson}
            onChange={setFindingsReviewJson}
            rows={14}
            maxLength={1_800_000}
          />
          <Button
            variant="primary"
            busy={busy === 'findings-review'}
            onClick={() =>
              void mutate('findings-review', () =>
                reviewAnalysisFindings(
                  id,
                  newIdempotencyKey(),
                  findingsReviewJson.trim()
                    ? parseObjectJson(findingsReviewJson, 'Findings review')
                    : { decisions: [] },
                ),
              )
            }
          >
            Record terminal decisions and freeze approved findings
          </Button>
        </div>
      ) : null}

      {day7.approvedFindings.length > 0 ? (
        <details style={{ ...card, marginTop: 12 }} open>
          <summary>
            Immutable approved findings ({day7.approvedFindings.length})
          </summary>
          <div style={{ display: 'grid', gap: 10, marginTop: 10 }}>
            {day7.approvedFindings.map((finding) => (
              <article key={finding.id} style={card}>
                <Tag tone="success">APPROVED FINDING</Tag>
                <h3 style={{ font: 'var(--type-heading-sm)' }}>
                  {typeof finding.payload['wording'] === 'string'
                    ? finding.payload['wording']
                    : 'Approved finding'}
                </h3>
                <pre style={{ whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>
                  {JSON.stringify(finding.payload, null, 2)}
                </pre>
              </article>
            ))}
          </div>
        </details>
      ) : null}

      {day7.reviewManifest ? (
        <InlineAlert
          tone={day7.approvedFindings.length > 0 ? 'success' : 'warning'}
          title={
            day7.approvedFindings.length > 0
              ? 'Findings review is complete. Strategic opportunities remain pending.'
              : 'Findings review completed with INSUFFICIENT_EVIDENCE. No finding was forced.'
          }
        />
      ) : null}
    </section>
  );
}
